import { useState, useRef, useEffect, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import api, { API } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { ArrowLeft, Send, ImageIcon, Flame, Check, CheckCheck, Ghost, Eye, Mic, Square, AlertTriangle, Play, Pause, Trash2, Reply, X, Smile, Bell, BellOff } from "lucide-react";
import { isMuted, toggleMute } from "@/lib/notifications";

const REACTION_EMOJIS = ["❤️", "😂", "👍", "🔥", "😮", "😢"];

export default function Chat() {
  const { friendId } = useParams();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { user } = useAuth();
  const [text, setText] = useState("");
  const [ephemeral, setEphemeral] = useState(false);
  const [urgent, setUrgent] = useState(false);
  const [destroyed, setDestroyed] = useState(new Set());
  const [recording, setRecording] = useState(false);
  const [recordTime, setRecordTime] = useState(0);
  const [pendingDelete, setPendingDelete] = useState(null);
  const [replyTo, setReplyTo] = useState(null);
  const [reactionsDetail, setReactionsDetail] = useState(null);
  const [muted, setMuted] = useState(isMuted(friendId));
  const fileRef = useRef(null);
  const bottomRef = useRef(null);
  const recorderRef = useRef(null);
  const chunksRef = useRef([]);
  const timerRef = useRef(null);

  const { data: friends = [] } = useQuery({
    queryKey: ["friends"],
    queryFn: async () => (await api.get("/friends")).data,
  });
  const friend = useMemo(() => friends.find((f) => f.user_id === friendId), [friends, friendId]);

  const { data: messages = [] } = useQuery({
    queryKey: ["messages", friendId],
    queryFn: async () => (await api.get(`/messages/${friendId}`)).data,
    refetchInterval: 3000,
  });

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  // schedule destruction of viewed incoming ephemeral messages
  useEffect(() => {
    messages.forEach((m) => {
      if (m.ephemeral && !m.mine && !destroyed.has(m.id)) {
        setTimeout(() => {
          setDestroyed((prev) => new Set(prev).add(m.id));
        }, 5000);
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [messages]);

  const refresh = () => {
    qc.invalidateQueries({ queryKey: ["messages", friendId] });
    qc.invalidateQueries({ queryKey: ["conversations"] });
  };

  const startReply = (m) => {
    const snippet = m.type === "photo" ? "📷 Photo" : m.type === "voice" ? "🎤 Message vocal" : m.content;
    setReplyTo({ id: m.id, snippet, author: m.mine ? "Vous" : (friend?.name || "Ami") });
  };

  const reactTo = async (id, emoji) => {
    try {
      await api.post(`/messages/${id}/react`, { emoji });
      refresh();
    } catch {
      toast.error("Réaction impossible");
    }
  };

  const showReactions = async (id) => {
    setReactionsDetail({ loading: true, list: [] });
    try {
      const res = await api.get(`/messages/${id}/reactions`);
      setReactionsDetail({ loading: false, list: res.data });
    } catch {
      setReactionsDetail(null);
    }
  };

  const deleteMessage = async (id) => {
    setDestroyed((prev) => new Set(prev).add(id));
    try {
      await api.delete(`/messages/${id}`);
      toast.success("Message supprimé");
      refresh();
    } catch {
      toast.error("Suppression impossible");
      setDestroyed((prev) => {
        const next = new Set(prev);
        next.delete(id);
        return next;
      });
      refresh();
    }
  };

  const sendText = async () => {
    if (!text.trim()) return;
    const fd = new FormData();
    fd.append("content", text.trim());
    fd.append("ephemeral", ephemeral);
    fd.append("urgent", urgent);
    if (replyTo) fd.append("reply_to_id", replyTo.id);
    setText("");
    setReplyTo(null);
    try {
      await api.post(`/messages/${friendId}/text`, fd);
      refresh();
    } catch {
      toast.error("Échec de l'envoi");
    }
  };

  const sendPhoto = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    fd.append("ephemeral", ephemeral);
    fd.append("urgent", urgent);
    if (replyTo) fd.append("reply_to_id", replyTo.id);
    setReplyTo(null);
    toast.loading("Envoi de la photo…", { id: "photo" });
    try {
      await api.post(`/messages/${friendId}/photo`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Photo envoyée", { id: "photo" });
      refresh();
    } catch {
      toast.error("Échec de l'envoi", { id: "photo" });
    }
    e.target.value = "";
  };

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const recorder = new MediaRecorder(stream);
      chunksRef.current = [];
      recorder.ondataavailable = (ev) => {
        if (ev.data.size > 0) chunksRef.current.push(ev.data);
      };
      recorder.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());
        const blob = new Blob(chunksRef.current, { type: recorder.mimeType || "audio/webm" });
        const dur = recordTime;
        const fd = new FormData();
        fd.append("file", blob, "voice.webm");
        fd.append("duration", dur);
        fd.append("ephemeral", ephemeral);
        fd.append("urgent", urgent);
        if (replyTo) fd.append("reply_to_id", replyTo.id);
        setReplyTo(null);
        toast.loading("Envoi du vocal…", { id: "voice" });
        try {
          await api.post(`/messages/${friendId}/voice`, fd, { headers: { "Content-Type": "multipart/form-data" } });
          toast.success("Message vocal envoyé", { id: "voice" });
          refresh();
        } catch {
          toast.error("Échec de l'envoi", { id: "voice" });
        }
      };
      recorderRef.current = recorder;
      recorder.start();
      setRecording(true);
      setRecordTime(0);
      timerRef.current = setInterval(() => setRecordTime((t) => t + 1), 1000);
    } catch {
      toast.error("Micro inaccessible. Autorisez l'accès au microphone.");
    }
  };

  const stopRecording = (cancel = false) => {
    clearInterval(timerRef.current);
    setRecording(false);
    if (recorderRef.current && recorderRef.current.state !== "inactive") {
      if (cancel) {
        recorderRef.current.onstop = () => {
          recorderRef.current.stream?.getTracks?.().forEach((t) => t.stop());
        };
      }
      recorderRef.current.stop();
    }
  };

  useEffect(() => {
    setMuted(isMuted(friendId));
  }, [friendId]);

  const visibleMessages = messages.filter((m) => !destroyed.has(m.id));

  return (
    <div className="min-h-screen bg-[#050505] flex flex-col">
      <header
        className="glass sticky top-0 z-50 border-b border-white/5 py-3 px-4 flex items-center gap-3"
        style={{ paddingTop: "calc(0.75rem + env(safe-area-inset-top))" }}
      >
        <button data-testid="chat-back-button" onClick={() => navigate("/app")} className="p-2 rounded-full hover:bg-white/5 transition">
          <ArrowLeft className="h-5 w-5" />
        </button>
        <Avatar className="h-10 w-10 border border-white/10">
          <AvatarImage src={friend?.picture} />
          <AvatarFallback className="bg-[#1A1A1A] text-[#C4A47C]">{friend?.name?.[0]?.toUpperCase() || "?"}</AvatarFallback>
        </Avatar>
        <div className="flex-1 min-w-0">
          <p className="font-medium truncate" data-testid="chat-friend-name">{friend?.name || "Discussion"}</p>
          <p className="text-xs text-[#8A8A8A]">Wado</p>
        </div>
        {user?.ghost_mode && (
          <div className="flex items-center gap-1.5 rounded-full border border-[#A3B8CC]/30 px-3 py-1.5 ghost-glow">
            <Ghost className="h-3.5 w-3.5 text-[#A3B8CC]" strokeWidth={1.5} />
            <span className="text-[10px] tracking-[0.15em] uppercase font-bold text-[#A3B8CC]">Fantôme</span>
          </div>
        )}
        <button
          data-testid="mute-conversation-button"
          onClick={() => { const m = toggleMute(friendId); setMuted(m); toast.success(m ? "Conversation en sourdine" : "Notifications réactivées"); }}
          title={muted ? "Réactiver les notifications" : "Mettre en sourdine"}
          className={`p-2 rounded-full transition ${muted ? "text-[#C4A47C] bg-[#C4A47C]/10" : "text-[#8A8A8A] hover:text-white hover:bg-white/5"}`}
        >
          {muted ? <BellOff className="h-5 w-5" /> : <Bell className="h-5 w-5" />}
        </button>
      </header>

      <main className="flex-1 max-w-2xl w-full mx-auto px-4 sm:px-6 py-6 pb-40">
        {visibleMessages.length === 0 ? (
          <div className="text-center py-20">
            <p className="text-[#8A8A8A]">Aucun message. Dites bonjour 👋</p>
          </div>
        ) : (
          <div className="flex flex-col gap-3">
            <AnimatePresence>
              {visibleMessages.map((m) => (
                <MessageBubble key={m.id} m={m} ghost={user?.ghost_mode} apiBase={API} onDelete={setPendingDelete} onReply={startReply} onReact={reactTo} onShowReactions={showReactions} />
              ))}
            </AnimatePresence>
            <div ref={bottomRef} />
          </div>
        )}
      </main>

      <div
        className="fixed bottom-6 left-4 right-4 max-w-2xl mx-auto z-50"
        style={{ bottom: "calc(1.5rem + env(safe-area-inset-bottom))" }}
      >
        {replyTo && !recording && (
          <div className="mb-2 flex items-center gap-3 glass border border-white/10 rounded-2xl px-4 py-2.5" data-testid="reply-preview">
            <Reply className="h-4 w-4 text-[#C4A47C] shrink-0" />
            <div className="flex-1 min-w-0">
              <p className="text-[10px] tracking-wide font-bold text-[#C4A47C]">Réponse à {replyTo.author}</p>
              <p className="text-xs text-[#8A8A8A] truncate">{replyTo.snippet}</p>
            </div>
            <button data-testid="reply-cancel-button" onClick={() => setReplyTo(null)} className="p-1.5 rounded-full text-[#8A8A8A] hover:text-white hover:bg-white/10 transition">
              <X className="h-4 w-4" />
            </button>
          </div>
        )}
        {recording ? (
          <div className="glass border border-red-500/40 rounded-full px-5 py-3 flex items-center gap-4 shadow-[0_8px_32px_rgba(0,0,0,0.5)]" data-testid="voice-recording-bar">
            <span className="h-3 w-3 rounded-full bg-red-500 animate-pulse" />
            <span className="text-sm font-medium text-red-400 flex-1">Enregistrement… {fmtTime(recordTime)}</span>
            <button
              data-testid="voice-cancel-button"
              onClick={() => stopRecording(true)}
              className="text-[#8A8A8A] hover:text-white text-sm transition px-2"
            >
              Annuler
            </button>
            <button
              data-testid="voice-stop-send-button"
              onClick={() => stopRecording(false)}
              className="p-2.5 rounded-full bg-white text-black hover:bg-gray-200 transition"
            >
              <Send className="h-5 w-5" strokeWidth={2} />
            </button>
          </div>
        ) : (
          <div className="glass border border-white/10 rounded-full px-4 py-2.5 flex items-center gap-1.5 shadow-[0_8px_32px_rgba(0,0,0,0.5)]">
            <button
              data-testid="urgent-toggle-button"
              onClick={() => setUrgent((v) => !v)}
              title="Marquer comme urgent"
              className={`p-2.5 rounded-full transition-colors duration-300 ${urgent ? "bg-red-500 text-white" : "text-[#8A8A8A] hover:text-white"}`}
            >
              <AlertTriangle className="h-5 w-5" strokeWidth={2} />
            </button>
            <button
              data-testid="ephemeral-toggle-button"
              onClick={() => setEphemeral((v) => !v)}
              title="Message éphémère"
              className={`p-2.5 rounded-full transition-colors duration-300 ${ephemeral ? "bg-[#C4A47C] text-black" : "text-[#8A8A8A] hover:text-white"}`}
            >
              <Flame className="h-5 w-5" strokeWidth={2} />
            </button>
            <button
              data-testid="chat-photo-capture-button"
              onClick={() => fileRef.current?.click()}
              className="p-2.5 rounded-full text-[#8A8A8A] hover:text-white transition"
            >
              <ImageIcon className="h-5 w-5" strokeWidth={2} />
            </button>
            <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={sendPhoto} data-testid="chat-photo-input" />
            <input
              data-testid="chat-message-input"
              value={text}
              onChange={(e) => setText(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && sendText()}
              placeholder={ephemeral ? "Message éphémère…" : urgent ? "Message urgent…" : "Votre message…"}
              className="flex-1 bg-transparent text-sm placeholder:text-[#8A8A8A] focus:outline-none px-2"
            />
            {text.trim() ? (
              <button
                data-testid="chat-send-text-button"
                onClick={sendText}
                className="p-2.5 rounded-full bg-white text-black hover:bg-gray-200 transition"
              >
                <Send className="h-5 w-5" strokeWidth={2} />
              </button>
            ) : (
              <button
                data-testid="voice-record-button"
                onClick={startRecording}
                title="Message vocal"
                className="p-2.5 rounded-full bg-white text-black hover:bg-gray-200 transition"
              >
                <Mic className="h-5 w-5" strokeWidth={2} />
              </button>
            )}
          </div>
        )}
        {(ephemeral || urgent) && !recording && (
          <p className={`text-center text-[10px] tracking-[0.15em] uppercase font-bold mt-2 ${urgent ? "text-red-400" : "text-[#C4A47C]"}`}>
            {urgent && "⚠ Urgent"}{urgent && ephemeral && " · "}{ephemeral && "Éphémère — disparaît après lecture"}
          </p>
        )}
      </div>

      <AlertDialog open={!!pendingDelete} onOpenChange={(open) => !open && setPendingDelete(null)}>
        <AlertDialogContent className="bg-[#121212] border border-white/10 text-white" data-testid="delete-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-display">Supprimer ce message ?</AlertDialogTitle>
            <AlertDialogDescription className="text-[#8A8A8A]">
              Cette action est définitive. Le message sera supprimé pour vous et votre interlocuteur.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel
              data-testid="delete-cancel-button"
              className="bg-[#1A1A1A] border border-white/10 text-white hover:bg-[#2A2A2A] hover:text-white rounded-full"
            >
              Annuler
            </AlertDialogCancel>
            <AlertDialogAction
              data-testid="delete-confirm-button"
              onClick={() => { const id = pendingDelete; setPendingDelete(null); deleteMessage(id); }}
              className="bg-red-500 text-white hover:bg-red-600 rounded-full"
            >
              Supprimer
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={!!reactionsDetail} onOpenChange={(open) => !open && setReactionsDetail(null)}>
        <AlertDialogContent className="bg-[#121212] border border-white/10 text-white" data-testid="reactions-detail-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-display">Réactions</AlertDialogTitle>
            <AlertDialogDescription className="text-[#8A8A8A]">
              Qui a réagi à ce message.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="flex flex-col gap-2 max-h-[50vh] overflow-y-auto">
            {reactionsDetail?.loading ? (
              <p className="text-[#8A8A8A] text-sm">Chargement…</p>
            ) : reactionsDetail?.list?.length ? (
              reactionsDetail.list.map((r, i) => (
                <div key={i} className="flex items-center gap-3 bg-[#1A1A1A] border border-white/5 rounded-xl px-3 py-2" data-testid="reaction-detail-item">
                  <span className="text-xl">{r.emoji}</span>
                  <img src={r.user.picture} alt="" className="h-8 w-8 rounded-full bg-[#2A2A2A] object-cover border border-white/10" />
                  <span className="text-sm flex-1 truncate">{r.mine ? "Vous" : r.user.name}</span>
                </div>
              ))
            ) : (
              <p className="text-[#8A8A8A] text-sm">Aucune réaction.</p>
            )}
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-[#1A1A1A] border border-white/10 text-white hover:bg-[#2A2A2A] hover:text-white rounded-full">
              Fermer
            </AlertDialogCancel>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function fmtTime(s) {
  const m = Math.floor(s / 60);
  const sec = s % 60;
  return `${m}:${String(sec).padStart(2, "0")}`;
}

function VoicePlayer({ src, mine }) {
  const audioRef = useRef(null);
  const [playing, setPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const [dur, setDur] = useState(0);

  const toggle = () => {
    const a = audioRef.current;
    if (!a) return;
    if (playing) {
      a.pause();
    } else {
      a.play();
    }
  };

  return (
    <div className="flex items-center gap-3 min-w-[180px]" data-testid="voice-player">
      <button
        onClick={(e) => { e.stopPropagation(); toggle(); }}
        className={`h-9 w-9 rounded-full flex items-center justify-center shrink-0 ${mine ? "bg-white text-black" : "bg-[#2A2A2A] text-white"}`}
      >
        {playing ? <Pause className="h-4 w-4" /> : <Play className="h-4 w-4 ml-0.5" />}
      </button>
      <div className="flex-1">
        <div className="h-1.5 rounded-full bg-white/10 overflow-hidden">
          <div className="h-full bg-[#C4A47C] transition-all" style={{ width: `${dur ? (progress / dur) * 100 : 0}%` }} />
        </div>
        <div className="flex items-center gap-1 mt-1.5">
          <Mic className="h-3 w-3 text-[#8A8A8A]" />
          <span className="text-[10px] text-[#8A8A8A]">{fmtTime(Math.floor(progress || dur || 0))}</span>
        </div>
      </div>
      <audio
        ref={audioRef}
        src={src}
        onPlay={() => setPlaying(true)}
        onPause={() => setPlaying(false)}
        onEnded={() => { setPlaying(false); setProgress(0); }}
        onLoadedMetadata={(e) => { const d = e.target.duration; setDur(isFinite(d) ? d : 0); }}
        onTimeUpdate={(e) => setProgress(e.target.currentTime)}
      />
    </div>
  );
}

function MessageBubble({ m, ghost, apiBase, onDelete, onReply, onReact, onShowReactions }) {
  const mine = m.mine;
  const [showPicker, setShowPicker] = useState(false);
  const borderClass = m.urgent
    ? "border border-red-500/60"
    : m.ephemeral
    ? "border border-[#C4A47C]/40"
    : "border border-white/5";
  const testid = m.urgent ? "urgent-message-bubble" : m.ephemeral ? "ephemeral-message-bubble" : "message-bubble";
  return (
    <motion.div
      layout
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, filter: "blur(12px)", scale: 0.95, transition: { duration: 0.8 } }}
      transition={{ duration: 0.3 }}
      className={`flex flex-col ${mine ? "items-end" : "items-start"}`}
      data-testid={testid}
    >
      <motion.div
        drag="x"
        dragConstraints={{ left: 0, right: 0 }}
        dragElastic={{ left: 0, right: 0.6 }}
        dragSnapToOrigin
        onDragEnd={(e, info) => { if (info.offset.x > 45) onReply(m); }}
        className="relative"
        data-testid="message-drag-wrapper"
      >
      <div className={`group flex items-center gap-1.5 ${mine ? "flex-row" : "flex-row-reverse"} max-w-full`}>
        <div className="flex items-center gap-0.5 shrink-0">
          <div className="relative">
            <button
              data-testid="react-message-button"
              onClick={() => setShowPicker((v) => !v)}
              title="Réagir"
              className="p-2 rounded-full text-[#8A8A8A] hover:text-[#C4A47C] hover:bg-[#C4A47C]/10 opacity-50 sm:opacity-0 group-hover:opacity-100 focus:opacity-100 focus-visible:opacity-100 focus:outline-none transition duration-200"
            >
              <Smile className="h-4 w-4" />
            </button>
            {showPicker && (
              <div
                data-testid="reaction-picker"
                className="absolute z-50 bottom-full mb-1 left-1/2 -translate-x-1/2 flex items-center gap-1 glass border border-white/10 rounded-full px-2 py-1.5 shadow-[0_8px_32px_rgba(0,0,0,0.5)]"
              >
                {REACTION_EMOJIS.map((e) => (
                  <button
                    key={e}
                    onClick={() => { onReact(m.id, e); setShowPicker(false); }}
                    className="text-lg hover:scale-125 transition-transform duration-150"
                  >
                    {e}
                  </button>
                ))}
              </div>
            )}
          </div>
          <button
            data-testid="reply-message-button"
            onClick={() => onReply(m)}
            title="Répondre"
            className="p-2 rounded-full text-[#8A8A8A] hover:text-[#C4A47C] hover:bg-[#C4A47C]/10 opacity-50 sm:opacity-0 group-hover:opacity-100 focus:opacity-100 focus-visible:opacity-100 focus:outline-none transition duration-200"
          >
            <Reply className="h-4 w-4" />
          </button>
          {mine && (
            <button
              data-testid="delete-message-button"
              onClick={() => onDelete(m.id)}
              title="Supprimer"
              className="p-2 rounded-full text-[#8A8A8A] hover:text-red-400 hover:bg-red-500/10 opacity-50 sm:opacity-0 group-hover:opacity-100 focus:opacity-100 focus-visible:opacity-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-red-500/50 transition duration-200"
            >
              <Trash2 className="h-4 w-4" />
            </button>
          )}
        </div>
        <div
          onClick={() => onReply(m)}
          className={`max-w-[78%] rounded-2xl px-4 py-2.5 cursor-pointer ${mine ? "bg-[#2A2A2A]" : "bg-[#121212]"} ${borderClass} ${m.urgent ? "shadow-[0_0_20px_rgba(239,68,68,0.25)]" : ""}`}
        >
          {m.reply_to && (
            <div className="mb-1.5 border-l-2 border-[#C4A47C]/60 pl-2 py-0.5" data-testid="reply-quote">
              <p className="text-[10px] tracking-wide font-bold text-[#C4A47C]">Réponse</p>
              <p className="text-xs text-[#8A8A8A] truncate max-w-[220px]">{m.reply_to.snippet}</p>
            </div>
          )}
          {m.urgent && (
            <div className="flex items-center gap-1 mb-1">
              <AlertTriangle className="h-3 w-3 text-red-400" />
              <span className="text-[10px] tracking-[0.15em] uppercase font-bold text-red-400">Urgent</span>
            </div>
          )}
          {m.ephemeral && (
            <div className="flex items-center gap-1 mb-1">
              <Flame className="h-3 w-3 text-[#C4A47C]" />
              <span className="text-[10px] tracking-[0.15em] uppercase font-bold text-[#C4A47C]">Éphémère</span>
            </div>
          )}
          {m.type === "text" ? (
            <p className="text-sm leading-relaxed break-words">{m.content}</p>
          ) : m.type === "voice" ? (
            <VoicePlayer src={`${apiBase}/files/${m.file_path}`} mine={mine} />
          ) : (
            <img
              src={`${apiBase}/files/${m.file_path}`}
              alt="photo"
              className="rounded-xl max-h-[60vh] w-auto"
              data-testid="chat-photo-message"
            />
          )}
        </div>
      </div>
      </motion.div>
      {m.reactions && m.reactions.length > 0 && (
        <div className={`flex flex-wrap gap-1 mt-1 px-1 ${mine ? "justify-end" : "justify-start"}`} data-testid="message-reactions">
          {m.reactions.map((r) => (
            <motion.button
              key={r.emoji}
              layout
              initial={{ scale: 0, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ type: "spring", stiffness: 500, damping: 18 }}
              onClick={() => onShowReactions(m.id)}
              className={`flex items-center gap-1 rounded-full px-2 py-0.5 text-xs border transition ${r.mine ? "bg-[#C4A47C]/20 border-[#C4A47C]/50" : "bg-[#1A1A1A] border-white/10"}`}
            >
              <motion.span key={r.count} initial={{ scale: 1.6 }} animate={{ scale: 1 }} transition={{ type: "spring", stiffness: 500, damping: 15 }}>
                {r.emoji}
              </motion.span>
              <span className="text-[10px] text-[#8A8A8A]">{r.count}</span>
            </motion.button>
          ))}
        </div>
      )}
      {mine && (
        <div className="flex items-center gap-1 mt-1 px-1" data-testid="read-receipt-indicator">
          {m.read || m.opened ? (
            <><CheckCheck className="h-3.5 w-3.5 text-[#A3B8CC]" /><span className="text-[10px] text-[#A3B8CC]">Vu</span></>
          ) : (
            <><Check className="h-3.5 w-3.5 text-[#8A8A8A]" /><span className="text-[10px] text-[#8A8A8A]">Envoyé</span></>
          )}
        </div>
      )}
      {!mine && ghost && (
        <div className="flex items-center gap-1 mt-1 px-1">
          <Eye className="h-3 w-3 text-[#A3B8CC]/60" />
          <span className="text-[10px] text-[#A3B8CC]/60">Lu en mode fantôme</span>
        </div>
      )}
    </motion.div>
  );
}
